// Per-action tool message dispatch table.
//
// RFC-170 (bag-tool decomposition) replaced action-bag tools (e.g. `documents`
// + args.action) with per-action tools (e.g. `documentsSearch`). Each
// per-action tool gets its own React component. Components are grouped by
// namespace in this directory; this file maps tool name → component and
// supplies a default icon for the chat message header.
import type { ReactNode } from 'react'
import {
  BookOpen,
  CalendarClock,
  ClipboardCheck,
  ClipboardList,
  Cloud,
  Database,
  FileText,
  GitCompare,
  Github,
  ListTodo,
  PackageCheck,
  Plug,
  ScanSearch,
  Search,
  Shield,
  Users,
  Wrench,
  type LucideIcon,
} from 'lucide-react'
import { ArtifactsSaveToolMessage } from './ArtifactsToolMessages'
import {
  AssetsListToolMessage,
  AssetsGetToolMessage,
  AssetsSearchToolMessage,
  AssetsStatsToolMessage,
  AssetsAnalyzeGapToolMessage,
  AssetsCreateToolMessage,
  AssetsUpdateToolMessage,
  AssetsClassifyToolMessage,
  AssetsMarkReviewedToolMessage,
  AssetsListProvidersToolMessage,
  AssetsSyncNowToolMessage,
} from './AssetsToolMessages'
import {
  AuditGetActivityFeedToolMessage,
  AuditGetLogsToolMessage,
  AuditToolMessage,
} from './AuditToolMessages'
import {
  ChecksGetCheckToolMessage,
  ChecksListChecksToolMessage,
  ChecksLinkedTestsToolMessage,
  ChecksHistoryToolMessage,
  ChecksRunToolMessage,
  ChecksScanToolMessage,
  ChecksCloudToolMessage,
  ChecksCodeToolMessage,
  ChecksSaveFindingToolMessage,
  ChecksDetectDriftToolMessage,
  ChecksRemedyPrepareToolMessage,
  ChecksRemedyValidateToolMessage,
  ChecksRemedyExecuteToolMessage,
  ChecksRemedyUndoToolMessage,
} from './ChecksToolMessages'
import { CisLookupToolMessage } from './CisToolMessages'
import {
  ClaimsListToolMessage,
  ClaimsFindToolMessage,
  ClaimsRecordToolMessage,
  ClaimsEditToolMessage,
  ClaimsDeleteToolMessage,
} from './ClaimsToolMessages'
import {
  ControlsGetToolMessage,
  ControlsListToolMessage,
} from './ControlsToolMessages'
import {
  DocumentsSaveDraftToolMessage,
  DocumentsListToolMessage,
  DocumentsSearchToolMessage,
  DocumentsGetByIdToolMessage,
  VaultDocumentToolMessage,
} from './DocumentsToolMessages'
import {
  DriftAcceptToolMessage,
  DriftDismissToolMessage,
  DriftProposeToolMessage,
} from './DriftToolMessages'
import {
  EvidenceListEvidenceToolMessage,
  EvidenceGetEvidenceToolMessage,
  EvidenceFulfillWithPolicyToolMessage,
  EvidenceFulfillWithDocumentToolMessage,
  EvidenceFulfillWithReportToolMessage,
  EvidenceFulfillTestToolMessage,
  EvidenceSaveUploadedFileToolMessage,
  EvidenceSubmitForReviewToolMessage,
  EvidenceWithdrawSubmissionToolMessage,
  EvidenceReviewSubmissionToolMessage,
  EvidenceApproveWithStatusSyncToolMessage,
} from './EvidenceToolMessages'
import {
  FindingsTriageToolMessage,
  FindingsSearchToolMessage,
  FindingsGetToolMessage,
  FindingsVerifyToolMessage,
  FindingsCloseToolMessage,
  FindingsUpdateToolMessage,
  FindingsAcceptRiskToolMessage,
  FindingsFalsePositiveToolMessage,
} from './FindingsToolMessages'
import {
  IntegrationsListToolMessage,
  IntegrationsGetToolMessage,
  IntegrationsGetRepoSettingsToolMessage,
  IntegrationsRequestConnectToolMessage,
} from './IntegrationsToolMessages'
import {
  PoliciesListToolMessage,
  PoliciesGetContentToolMessage,
  PoliciesGroundToolMessage,
  PoliciesGenerateToolMessage,
} from './PoliciesToolMessages'
import {
  PopulationListUsersToolMessage,
  PopulationListPrivilegedUsersToolMessage,
  PopulationListCodeAccessUsersToolMessage,
  PopulationListIdentityCenterUsersToolMessage,
  PopulationListOrgMembersToolMessage,
  PopulationListComplianceRoleHoldersToolMessage,
  PopulationFindEmployeeToolMessage,
  PopulationDiffToolMessage,
} from './PopulationToolMessages'
import {
  ProgramGetStateToolMessage,
  ProgramGetReadinessToolMessage,
  ProgramGetTimelineToolMessage,
  AuditPackageReadinessToolMessage,
} from './ProgramToolMessages'
import {
  RisksListToolMessage,
  RisksGetToolMessage,
  RisksSearchToolMessage,
  RisksStatsToolMessage,
  RisksLinkControlToolMessage,
  RisksUnlinkControlToolMessage,
  RisksLinkAssetToolMessage,
  RisksUnlinkAssetToolMessage,
  RisksLinkVendorToolMessage,
  RisksUnlinkVendorToolMessage,
  RisksUpdateToolMessage,
} from './RisksToolMessages'
import {
  RunbookCatalogListActionsToolMessage,
  RunbookCatalogDescribeActionToolMessage,
  RunbookCatalogSearchActionsToolMessage,
  RunbookCatalogListConnectedProvidersToolMessage,
} from './RunbookCatalogToolMessages'
import {
  RunbookExecutionExecuteToolMessage,
  RunbookExecutionStatusToolMessage,
  RunbookExecutionResumeToolMessage,
  RunbookExecutionApproveToolMessage,
  RunbookExecutionRejectToolMessage,
} from './RunbookExecutionToolMessages'
import {
  RunbookSearchToolMessage,
  RunbookGetToolMessage,
  RunbookUpdateToolMessage,
  RunbookEditToolMessage,
  RunbookImproveToolMessage,
  RunbookLinkToolMessage,
} from './RunbookToolMessages'
import {
  SchedulingListToolMessage,
  SchedulingGetDeadlinesToolMessage,
} from './SchedulingToolMessages'
import { LoadSkillToolMessage } from './SkillToolMessages'
import {
  TestsListToolMessage,
  TestsGetToolMessage,
  TestsListSubtasksToolMessage,
  TestsAssignToolMessage,
  TestsUpdateStatusToolMessage,
  TestsCompleteToolMessage,
  TestsSetDueDateToolMessage,
  TestsAssignSubtaskToolMessage,
  TestsBulkAssignToolMessage,
  TestsCreateToolMessage,
  TestsConvertToolMessage,
  ApiTestRunStartToolMessage,
  ApiTestRunListToolMessage,
  ApiTestRunGetToolMessage,
  SearchNativeChecksToolMessage,
  ApiCheckBindToolMessage,
} from './TestsToolMessages'
import {
  TicketsListToolMessage,
  TicketsGetToolMessage,
  TicketsCreateToolMessage,
  TicketsUpdateToolMessage,
  TicketsCloseToolMessage,
  TicketsDeleteToolMessage,
} from './TicketsToolMessages'
// FindExternalTicketsToolMessage intentionally not imported — the
// findExternalTickets agent tool was retired (all work-tracking search
// flows through findings*, and per-test evidence discovery flows through
// the checks* tools).
import {
  VendorsListToolMessage,
  VendorsGetToolMessage,
  VendorsSearchToolMessage,
  VendorsCreateToolMessage,
  VendorsAssessToolMessage,
  VendorsRequestEvidenceToolMessage,
} from './VendorsToolMessages'
import type { ToolPartProps } from './_base'

type PerActionToolComponent = React.ComponentType<ToolPartProps>

export const PER_ACTION_TOOL_COMPONENTS: Record<
  string,
  PerActionToolComponent
> = {
  // skills
  loadSkill: LoadSkillToolMessage,

  // runbook
  runbookSearch: RunbookSearchToolMessage,
  runbookGet: RunbookGetToolMessage,
  runbookUpdate: RunbookUpdateToolMessage,
  runbookEdit: RunbookEditToolMessage,
  runbookImprove: RunbookImproveToolMessage,
  runbookLink: RunbookLinkToolMessage,

  // runbook execution
  runbookExecutionExecute: RunbookExecutionExecuteToolMessage,
  runbookExecutionStatus: RunbookExecutionStatusToolMessage,
  runbookExecutionResume: RunbookExecutionResumeToolMessage,
  runbookExecutionApprove: RunbookExecutionApproveToolMessage,
  runbookExecutionReject: RunbookExecutionRejectToolMessage,

  // runbook catalog
  runbookCatalogListActions: RunbookCatalogListActionsToolMessage,
  runbookCatalogDescribeAction: RunbookCatalogDescribeActionToolMessage,
  runbookCatalogSearchActions: RunbookCatalogSearchActionsToolMessage,
  runbookCatalogListConnectedProviders:
    RunbookCatalogListConnectedProvidersToolMessage,

  // documents
  documentsSaveDraft: DocumentsSaveDraftToolMessage,
  documentsList: DocumentsListToolMessage,
  documentsSearch: DocumentsSearchToolMessage,
  documentsGetById: DocumentsGetByIdToolMessage,
  'vault-document': VaultDocumentToolMessage,

  // policies
  policiesList: PoliciesListToolMessage,
  policiesGetContent: PoliciesGetContentToolMessage,
  policiesGround: PoliciesGroundToolMessage,
  policiesGenerate: PoliciesGenerateToolMessage,

  // claims
  claimsList: ClaimsListToolMessage,
  claimsFind: ClaimsFindToolMessage,
  claimsRecord: ClaimsRecordToolMessage,
  claimsEdit: ClaimsEditToolMessage,
  claimsDelete: ClaimsDeleteToolMessage,

  // evidence
  evidenceListEvidence: EvidenceListEvidenceToolMessage,
  evidenceGetEvidence: EvidenceGetEvidenceToolMessage,
  evidenceFulfillWithPolicy: EvidenceFulfillWithPolicyToolMessage,
  evidenceFulfillWithDocument: EvidenceFulfillWithDocumentToolMessage,
  evidenceFulfillWithReport: EvidenceFulfillWithReportToolMessage,
  evidenceFulfillTest: EvidenceFulfillTestToolMessage,
  evidenceSaveUploadedFile: EvidenceSaveUploadedFileToolMessage,
  evidenceSubmitForReview: EvidenceSubmitForReviewToolMessage,
  evidenceWithdrawSubmission: EvidenceWithdrawSubmissionToolMessage,
  evidenceReviewSubmission: EvidenceReviewSubmissionToolMessage,
  evidenceApproveWithStatusSync: EvidenceApproveWithStatusSyncToolMessage,

  // program
  programGetState: ProgramGetStateToolMessage,
  programGetReadiness: ProgramGetReadinessToolMessage,
  programGetTimeline: ProgramGetTimelineToolMessage,
  auditPackageReadiness: AuditPackageReadinessToolMessage,

  // cis
  cisLookup: CisLookupToolMessage,

  // checks
  checksGet: ChecksGetCheckToolMessage,
  checksGetCheck: ChecksGetCheckToolMessage,
  checksList: ChecksListChecksToolMessage,
  checksListChecks: ChecksListChecksToolMessage,
  checksLinkedTests: ChecksLinkedTestsToolMessage,
  checksHistory: ChecksHistoryToolMessage,
  checksScan: ChecksScanToolMessage,
  checksRun: ChecksRunToolMessage,
  checksCloud: ChecksCloudToolMessage,
  checksCode: ChecksCodeToolMessage,
  checksSaveFinding: ChecksSaveFindingToolMessage,
  checksDetectDrift: ChecksDetectDriftToolMessage,
  checksRemedyPrepare: ChecksRemedyPrepareToolMessage,
  checksRemedyValidate: ChecksRemedyValidateToolMessage,
  checksRemedyExecute: ChecksRemedyExecuteToolMessage,
  checksRemedyUndo: ChecksRemedyUndoToolMessage,

  // controls
  controlsList: ControlsListToolMessage,
  controlsGet: ControlsGetToolMessage,

  // scheduling
  schedulingList: SchedulingListToolMessage,
  schedulingGetDeadlines: SchedulingGetDeadlinesToolMessage,

  // findings
  findingsTriage: FindingsTriageToolMessage,
  findingsSearch: FindingsSearchToolMessage,
  findingsGet: FindingsGetToolMessage,
  findingsVerify: FindingsVerifyToolMessage,
  findingsClose: FindingsCloseToolMessage,
  findingsUpdate: FindingsUpdateToolMessage,
  findingsAcceptRisk: FindingsAcceptRiskToolMessage,
  findingsFalsePositive: FindingsFalsePositiveToolMessage,

  // tickets
  ticketsList: TicketsListToolMessage,
  ticketsGet: TicketsGetToolMessage,
  ticketsCreate: TicketsCreateToolMessage,
  ticketsUpdate: TicketsUpdateToolMessage,
  ticketsClose: TicketsCloseToolMessage,
  ticketsDelete: TicketsDeleteToolMessage,

  // vendors
  vendorsList: VendorsListToolMessage,
  vendorsGet: VendorsGetToolMessage,
  vendorsSearch: VendorsSearchToolMessage,
  vendorsCreate: VendorsCreateToolMessage,
  vendorsAssess: VendorsAssessToolMessage,
  vendorsRequestEvidence: VendorsRequestEvidenceToolMessage,

  // risks
  risksList: RisksListToolMessage,
  risksGet: RisksGetToolMessage,
  risksSearch: RisksSearchToolMessage,
  risksStats: RisksStatsToolMessage,
  risksLinkControl: RisksLinkControlToolMessage,
  risksUnlinkControl: RisksUnlinkControlToolMessage,
  risksLinkAsset: RisksLinkAssetToolMessage,
  risksUnlinkAsset: RisksUnlinkAssetToolMessage,
  risksLinkVendor: RisksLinkVendorToolMessage,
  risksUnlinkVendor: RisksUnlinkVendorToolMessage,
  risksUpdate: RisksUpdateToolMessage,

  // assets
  assetsList: AssetsListToolMessage,
  assetsGet: AssetsGetToolMessage,
  assetsSearch: AssetsSearchToolMessage,
  assetsStats: AssetsStatsToolMessage,
  assetsAnalyzeGap: AssetsAnalyzeGapToolMessage,
  assetsCreate: AssetsCreateToolMessage,
  assetsUpdate: AssetsUpdateToolMessage,
  assetsClassify: AssetsClassifyToolMessage,
  assetsMarkReviewed: AssetsMarkReviewedToolMessage,
  assetsListProviders: AssetsListProvidersToolMessage,
  assetsSyncNow: AssetsSyncNowToolMessage,

  // tests
  testsList: TestsListToolMessage,
  testsGet: TestsGetToolMessage,
  testsListChildren: TestsListSubtasksToolMessage,
  testsListSubtasks: TestsListSubtasksToolMessage,
  testsAssign: TestsAssignToolMessage,
  testsUpdateStatus: TestsUpdateStatusToolMessage,
  testsComplete: TestsCompleteToolMessage,
  testsSetDueDate: TestsSetDueDateToolMessage,
  testsAssignChild: TestsAssignSubtaskToolMessage,
  testsAssignSubtask: TestsAssignSubtaskToolMessage,
  testsBulkAssign: TestsBulkAssignToolMessage,
  testsCreate: TestsCreateToolMessage,
  testsConvert: TestsConvertToolMessage,
  apiTestRunStart: ApiTestRunStartToolMessage,
  apiTestRunList: ApiTestRunListToolMessage,
  apiTestRunGet: ApiTestRunGetToolMessage,
  searchNativeChecks: SearchNativeChecksToolMessage,
  apiCheckBind: ApiCheckBindToolMessage,

  // population
  populationListUsers: PopulationListUsersToolMessage,
  populationListPrivilegedUsers: PopulationListPrivilegedUsersToolMessage,
  populationListCodeAccessUsers: PopulationListCodeAccessUsersToolMessage,
  populationListIdentityCenterUsers:
    PopulationListIdentityCenterUsersToolMessage,
  populationListOrgMembers: PopulationListOrgMembersToolMessage,
  populationListComplianceRoleHolders:
    PopulationListComplianceRoleHoldersToolMessage,
  populationFindEmployee: PopulationFindEmployeeToolMessage,
  populationDiff: PopulationDiffToolMessage,

  // integrations
  integrationsList: IntegrationsListToolMessage,
  integrationsGet: IntegrationsGetToolMessage,
  integrationsGetRepoSettings: IntegrationsGetRepoSettingsToolMessage,
  integrationsRequestConnect: IntegrationsRequestConnectToolMessage,

  // artifacts
  artifactsSave: ArtifactsSaveToolMessage,

  // audit
  audit: AuditToolMessage,
  auditGetActivityFeed: AuditGetActivityFeedToolMessage,
  auditGetLogs: AuditGetLogsToolMessage,

  // drift
  driftPropose: DriftProposeToolMessage,
  driftAccept: DriftAcceptToolMessage,
  driftDismiss: DriftDismissToolMessage,
}

// Default header icons for the chat message bubble (the in-progress / done
// states are owned by each component).
const ICON_CLS = 'h-[16px] w-[16px] [&_svg]:size-[16px]'
function ic(C: LucideIcon): ReactNode {
  return <C className={ICON_CLS} strokeWidth={2} />
}

export const PER_ACTION_TOOL_ICONS: Record<string, ReactNode> = {
  loadSkill: ic(Wrench),

  runbookSearch: ic(ClipboardCheck),
  runbookGet: ic(ClipboardCheck),
  runbookUpdate: ic(ClipboardCheck),
  runbookEdit: ic(ClipboardCheck),
  runbookImprove: ic(ClipboardCheck),
  runbookLink: ic(ClipboardCheck),

  runbookExecutionExecute: ic(ClipboardCheck),
  runbookExecutionStatus: ic(ClipboardCheck),
  runbookExecutionResume: ic(ClipboardCheck),
  runbookExecutionApprove: ic(ClipboardCheck),
  runbookExecutionReject: ic(ClipboardCheck),

  runbookCatalogListActions: ic(BookOpen),
  runbookCatalogDescribeAction: ic(BookOpen),
  runbookCatalogSearchActions: ic(Search),
  runbookCatalogListConnectedProviders: ic(Plug),

  documentsSaveDraft: ic(FileText),
  documentsList: ic(FileText),
  documentsSearch: ic(FileText),
  documentsGetById: ic(FileText),
  'vault-document': ic(FileText),

  policiesList: ic(FileText),
  policiesGetContent: ic(FileText),
  policiesGround: ic(FileText),
  policiesGenerate: ic(FileText),

  claimsList: ic(ClipboardCheck),
  claimsFind: ic(ClipboardCheck),
  claimsRecord: ic(ClipboardCheck),
  claimsEdit: ic(ClipboardCheck),
  claimsDelete: ic(ClipboardCheck),

  evidenceListEvidence: ic(Database),
  evidenceGetEvidence: ic(Cloud),
  evidenceFulfillWithPolicy: ic(Database),
  evidenceFulfillWithDocument: ic(Database),
  evidenceFulfillWithReport: ic(Database),
  evidenceFulfillTest: ic(Database),
  evidenceSaveUploadedFile: ic(Database),
  evidenceSubmitForReview: ic(Database),
  evidenceWithdrawSubmission: ic(Database),
  evidenceReviewSubmission: ic(Database),
  evidenceApproveWithStatusSync: ic(Database),

  programGetState: ic(ClipboardList),
  programGetReadiness: ic(ClipboardCheck),
  programGetTimeline: ic(CalendarClock),
  auditPackageReadiness: ic(PackageCheck),

  cisLookup: ic(Shield),

  checksGet: ic(Shield),
  checksGetCheck: ic(Shield),
  checksList: ic(Shield),
  checksListChecks: ic(Shield),
  checksLinkedTests: ic(Shield),
  checksHistory: ic(Shield),
  checksScan: ic(ScanSearch),
  checksRun: ic(ScanSearch),
  checksCloud: ic(Cloud),
  checksCode: ic(Github),
  checksSaveFinding: ic(Shield),
  checksDetectDrift: ic(ScanSearch),
  checksRemedyPrepare: ic(Wrench),
  checksRemedyValidate: ic(Wrench),
  checksRemedyExecute: ic(Wrench),
  checksRemedyUndo: ic(Wrench),

  controlsList: ic(Shield),
  controlsGet: ic(Shield),

  schedulingList: ic(CalendarClock),
  schedulingGetDeadlines: ic(CalendarClock),

  findingsTriage: ic(Wrench),
  findingsSearch: ic(Wrench),
  findingsGet: ic(Wrench),
  findingsVerify: ic(Wrench),
  findingsClose: ic(Wrench),
  findingsUpdate: ic(Wrench),
  findingsAcceptRisk: ic(Wrench),
  findingsFalsePositive: ic(Wrench),

  ticketsList: ic(ClipboardList),
  ticketsGet: ic(ClipboardList),
  ticketsCreate: ic(ClipboardList),
  ticketsUpdate: ic(ClipboardList),
  ticketsClose: ic(ClipboardList),
  ticketsDelete: ic(ClipboardList),

  vendorsList: ic(Database),
  vendorsGet: ic(Database),
  vendorsSearch: ic(Database),
  vendorsCreate: ic(Database),
  vendorsAssess: ic(Database),
  vendorsRequestEvidence: ic(Database),

  risksList: ic(Shield),
  risksGet: ic(Shield),
  risksSearch: ic(Shield),
  risksStats: ic(Shield),
  risksLinkControl: ic(Shield),
  risksUnlinkControl: ic(Shield),
  risksLinkAsset: ic(Shield),
  risksUnlinkAsset: ic(Shield),
  risksLinkVendor: ic(Shield),
  risksUnlinkVendor: ic(Shield),
  risksUpdate: ic(Shield),

  assetsList: ic(Database),
  assetsGet: ic(Database),
  assetsSearch: ic(Database),
  assetsStats: ic(Database),
  assetsAnalyzeGap: ic(ScanSearch),
  assetsCreate: ic(Database),
  assetsUpdate: ic(Database),
  assetsClassify: ic(Database),
  assetsMarkReviewed: ic(Database),
  assetsListProviders: ic(Database),
  assetsSyncNow: ic(Database),

  testsList: ic(ListTodo),
  testsGet: ic(ListTodo),
  testsListChildren: ic(ListTodo),
  testsListSubtasks: ic(ListTodo),
  testsAssign: ic(ListTodo),
  testsUpdateStatus: ic(ListTodo),
  testsComplete: ic(ListTodo),
  testsSetDueDate: ic(ListTodo),
  testsAssignChild: ic(ListTodo),
  testsAssignSubtask: ic(ListTodo),
  testsBulkAssign: ic(ListTodo),
  testsCreate: ic(ListTodo),
  testsConvert: ic(ListTodo),
  searchNativeChecks: ic(ScanSearch),
  apiCheckBind: ic(Shield),

  populationListUsers: ic(Users),
  populationListPrivilegedUsers: ic(Users),
  populationListCodeAccessUsers: ic(Users),
  populationListIdentityCenterUsers: ic(Users),
  populationListOrgMembers: ic(Users),
  populationListEmployees: ic(Users),
  populationListAccessRoleHolders: ic(Users),
  populationListComplianceRoleHolders: ic(Users),
  populationFindEmployee: ic(Users),
  populationListDevices: ic(Users),
  populationList: ic(Users),
  populationDiff: ic(GitCompare),

  integrationsList: ic(Github),
  integrationsGet: ic(Github),
  integrationsGetRepoSettings: ic(Github),
  integrationsRequestConnect: ic(Plug),

  artifactsSave: ic(FileText),

  audit: ic(ClipboardList),
  auditGetActivityFeed: ic(ClipboardList),
  auditGetLogs: ic(ClipboardList),

  driftPropose: ic(GitCompare),
  driftAccept: ic(GitCompare),
  driftDismiss: ic(GitCompare),
}
